import { useLocation, useNavigate } from "react-router-dom";
import { BreadCrumbData } from "@shared/lib/navigation/Breadcrumbs.types";
import { Account } from "@models/account";
import { editLastBread, getLastAvailableLink } from "@features/tickets/shared/utils/breadcrumbs";
import {
  CUSTOMERS_BREADCRUMB,
  CUSTOMERS_PATH,
  CUSTOMERS_STRUCTURE_CRUMBS,
} from "./customers.constants";



/**
 * Builds breadcrumb trail for the customers section, preserving document-flow crumbs when present.
 * @param state router location state passed to editLastBread
 * @returns breadcrumb items for the customers list and parent ledger
 */
function buildCustomersBreadcrumb(state: unknown): BreadCrumbData[] {
  const base: BreadCrumbData[] = [
    { name: CUSTOMERS_BREADCRUMB.ledger },
    { name: CUSTOMERS_BREADCRUMB.list, link: CUSTOMERS_PATH },
  ];

  const stateRecord = state as { breadcrumbData?: BreadCrumbData[] } | null | undefined;
  return [...(editLastBread(stateRecord?.breadcrumbData, CUSTOMERS_PATH, state) ?? base)];
}



/**
 * Navigation helpers for the customers page: row pick in document flows and structure editor routes.
 * @returns rowChosen handler, structure edit navigators, and whether table rows are selectable
 */
export function useCustomersNavigation() {
  const location = useLocation();
  const navigate = useNavigate();



  /**
   * Selects a customer row and returns to the previous breadcrumb with document_account set.
   * @param row selected account row
   */
  function rowChosen<T extends Account>(row: T) {
    if (!location.state?.breadcrumbData) {
      return;
    }

    const target = getLastAvailableLink(location.state.breadcrumbData);

    if (location.state?.customerSelectFor === "car") {
      const rest = { ...location.state };
      delete rest.customerSelectFor;

      navigate(target, {
        replace: true,
        state: {
          ...rest,
          car: { ...location.state.car, account_number: row.account_number },
          breadcrumbData: location.state.breadcrumbData.slice(0, -1),
        },
      });

      return;
    }

    if (location.state?.customerSelectFor === "car-edit") {
      const rest = { ...location.state };
      delete rest.customerSelectFor;

      navigate(target, {
        replace: true,
        state: {
          ...rest,
          selectedAccountNumber: row.account_number,
          breadcrumbData: location.state.breadcrumbData.slice(0, -1),
        },
      });

      return;
    }

    navigate(target, {
      replace: true,
      state: {
        ...location.state,
        document_account: { ...row, id: undefined, account_id: row.id },
        car: null,
        breadcrumbData: location.state.breadcrumbData.slice(0, -1),
      },
    });
  }



  /**
   * Navigates to the browse structure (table columns) configuration screen.
   */
  function editBrowseStructure() {
    const currentState = { ...location.state };
    const breadcrumbData = buildCustomersBreadcrumb(currentState);

    navigate(`${CUSTOMERS_PATH}/structure/browse`, {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: CUSTOMERS_STRUCTURE_CRUMBS.browse }],
      },
    });
  }

  return {
    rowChosen,
    editBrowseStructure,
    selectRow: Boolean(location.state?.breadcrumbData),
  };
}
